import { appAlert } from '@/lib/appAlert';
import { Ionicons } from '@expo/vector-icons';
import { Link, useRouter } from 'expo-router';
import { useState } from 'react';
import {
  Image,
  ImageBackground,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { contentMaxWidth, fontSize, radius, spacing } from '@/constants/theme';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/contexts/AuthContext';
import { validateEmail, validatePassword } from '@/lib/validation';

const wallpaper = require('../../../assets/wallpaper.jpg');
const logo = require('../../../assets/logo.png');

const GOLD = '#C9A843';
const CARD_BG = 'rgba(13, 27, 42, 0.84)';
const CARD_BORDER = 'rgba(201, 168, 67, 0.22)';
const TEXT_WHITE = '#FFFFFF';
const TEXT_MUTED = 'rgba(255,255,255,0.58)';

export default function LoginScreen() {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const insets = useSafeAreaInsets();
  const router = useRouter();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit() {
    setFormError(null);
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);
    if (emailError || passwordError) {
      setErrors({ email: emailError ?? undefined, password: passwordError ?? undefined });
      return;
    }
    setErrors({});
    setLoading(true);
    const { error } = await signIn(email, password);
    setLoading(false);
    if (error === 'Λάθος email ή κωδικός.') {
      appAlert(
        t('auth.login.notRegistered_title'),
        t('auth.login.notRegistered_body'),
        [
          { text: t('auth.login.notRegistered_no'), style: 'cancel' },
          { text: t('auth.login.notRegistered_yes'), onPress: () => router.push('/(auth)/register') },
        ],
      );
      return;
    }
    if (error) setFormError(error);
  }

  return (
    <ImageBackground
      source={wallpaper}
      style={styles.flex}
      resizeMode={Platform.OS === 'web' ? 'repeat' : 'cover'}
    >
      <View style={styles.overlay} />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[
            styles.content,
            { paddingTop: insets.top + spacing.xxl, paddingBottom: insets.bottom + spacing.lg },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.logoWrap}>
            <Image source={logo} style={styles.logo} resizeMode="contain" />
          </View>

          <View style={styles.card}>
            <Text style={styles.subtitle}>{t('auth.login.subtitle')}</Text>

            <View style={styles.form}>
              <Input
                label="Email"
                value={email}
                onChangeText={setEmail}
                error={errors.email}
                placeholder="name@example.com"
                keyboardType="email-address"
                autoCapitalize="none"
                autoComplete="email"
                textContentType="emailAddress"
              />
              <Input
                label={t('auth.field.password')}
                value={password}
                onChangeText={setPassword}
                error={errors.password}
                placeholder="••••••••"
                secureTextEntry={!showPassword}
                autoCapitalize="none"
                autoComplete="password"
                textContentType="password"
                rightElement={
                  <Pressable onPress={() => setShowPassword((v) => !v)} hitSlop={8}>
                    <Ionicons name={showPassword ? 'eye-off-outline' : 'eye-outline'} size={20} color={TEXT_MUTED} />
                  </Pressable>
                }
              />

              <Link href="/(auth)/forgot-password" style={styles.forgotLink}>
                {t('auth.login.forgot_password')}
              </Link>

              {!!formError && <Text style={styles.formError}>{formError}</Text>}

              <Button title={t('auth.login.submit')} onPress={onSubmit} loading={loading} />
            </View>

            <View style={styles.footer}>
              <Text style={styles.footerText}>{t('auth.login.no_account')} </Text>
              <Link href="/(auth)/register" style={styles.footerLink}>
                {t('auth.login.register_link')}
              </Link>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </ImageBackground>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(13, 27, 42, 0.68)',
  },
  content: {
    // Σε μεγάλες οθόνες η φόρμα μένει κεντραρισμένη σε αναγνώσιμο πλάτος
    // αντί να τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
    width: '100%',
    maxWidth: contentMaxWidth.form,
    alignSelf: 'center',
    flexGrow: 1,
    paddingHorizontal: spacing.lg,
    gap: spacing.xl,
  },
  logoWrap: {
    alignItems: 'center',
  },
  logo: {
    width: 160,
    height: 160,
  },
  card: {
    backgroundColor: CARD_BG,
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: CARD_BORDER,
    padding: spacing.lg,
    gap: spacing.lg,
  },
  subtitle: {
    color: TEXT_MUTED,
    fontSize: fontSize.md,
    textAlign: 'center',
  },
  form: {
    gap: spacing.md,
  },
  forgotLink: {
    color: GOLD,
    fontSize: fontSize.sm,
    fontWeight: '600',
    alignSelf: 'flex-end',
    marginTop: -spacing.xs,
  },
  formError: {
    color: '#F87171',
    fontSize: fontSize.sm,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerText: {
    color: TEXT_MUTED,
    fontSize: fontSize.md,
  },
  footerLink: {
    color: GOLD,
    fontSize: fontSize.md,
    fontWeight: '600',
  },
});
